// FilterPanel.jsx — Trello-style right-drawer filter panel for a project board.
import React from 'react';
import { X, Filter as FilterIcon } from 'lucide-react';
import { getAvatarUrl } from '../lib/api';
import { CARD_LABEL_COLORS, PRIORITY_LABEL, priorityKey } from '../lib/constants';
import Portal from './Portal';

const initialsOf = (m) => (m?.full_name || m?.email || '?').split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase();

export const EMPTY_FILTERS = {
  keyword: '',
  members: [],       // user_ids
  no_members: false,
  mine_only: false,
  status: '',        // '' | 'done' | 'not_done'
  due: '',           // '' | 'no_date' | 'overdue' | 'today' | 'week' | 'month'
  priorities: [],    // list of priority keys
  labels: [],        // list of hex colors
};

export function filterMatches(task, f, currentUserId) {
  if (f.keyword) {
    const k = f.keyword.toLowerCase();
    const inTitle = (task.title || '').toLowerCase().includes(k);
    const inDesc = (task.description || '').toLowerCase().includes(k);
    const inNames = (task.assignees || []).some((a) => (a.full_name || '').toLowerCase().includes(k));
    if (!inTitle && !inDesc && !inNames) return false;
  }
  const assigneeIds = (task.assignees || []).map((a) => a.id);
  if (f.no_members && assigneeIds.length > 0) return false;
  if (f.mine_only && !assigneeIds.includes(currentUserId)) return false;
  if (f.members?.length && !f.members.some((id) => assigneeIds.includes(id))) return false;
  if (f.status === 'done' && !task.completed) return false;
  if (f.status === 'not_done' && task.completed) return false;
  if (f.priorities?.length && !f.priorities.includes(priorityKey(task))) return false;
  if (f.labels?.length && !f.labels.includes(task.label_color)) return false;
  if (f.due) {
    if (f.due === 'no_date') { if (task.due_date) return false; }
    else {
      if (!task.due_date) return false;
      const now = new Date(); now.setHours(0, 0, 0, 0);
      const d = new Date(task.due_date); d.setHours(0, 0, 0, 0);
      const diffDays = Math.round((d - now) / (24 * 3600 * 1000));
      if (f.due === 'overdue' && !(diffDays < 0 && !task.completed)) return false;
      if (f.due === 'today' && diffDays !== 0) return false;
      if (f.due === 'week' && !(diffDays >= 0 && diffDays <= 7)) return false;
      if (f.due === 'month' && !(diffDays >= 0 && diffDays <= 30)) return false;
    }
  }
  return true;
}

export function activeFilterCount(f) {
  let n = 0;
  if (f.keyword) n++;
  if (f.no_members) n++;
  if (f.mine_only) n++;
  if (f.members?.length) n += f.members.length;
  if (f.status) n++;
  if (f.due) n++;
  n += (f.priorities?.length || 0);
  n += (f.labels?.length || 0);
  return n;
}

export default function FilterPanel({ open, onClose, filters, setFilters, members = [] }) {
  if (!open) return null;
  const set = (patch) => setFilters({ ...filters, ...patch });
  const toggleArr = (key, val) => {
    const arr = new Set(filters[key] || []);
    if (arr.has(val)) arr.delete(val); else arr.add(val);
    set({ [key]: [...arr] });
  };
  const clearAll = () => setFilters(EMPTY_FILTERS);
  const count = activeFilterCount(filters);

  return (
    <Portal>
    <div className="tm-filter-panel" data-testid="filter-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FilterIcon size={16} />
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Filter</h3>
          {count > 0 && <span style={{ background: '#0079BF', color: '#fff', fontSize: 11, padding: '1px 6px', borderRadius: 10 }} data-testid="filter-count">{count}</span>}
        </div>
        <div className="flex items-center gap-2">
          {count > 0 && <button className="tm-filter-clear" onClick={clearAll} data-testid="filter-clear-all">Clear all</button>}
          <button className="tm-btn-ghost" onClick={onClose} data-testid="filter-close"><X size={16} /></button>
        </div>
      </div>

      <div className="tm-filter-section">
        <h5>Keyword</h5>
        <input className="tm-input" placeholder="Search cards, members, labels…"
          value={filters.keyword} onChange={(e) => set({ keyword: e.target.value })} data-testid="filter-keyword" />
        <div style={{ fontSize: 11, color: 'var(--trello-text-muted)', marginTop: 4 }}>
          Cards where title, description or assignee names include this text.
        </div>
      </div>

      <div className="tm-filter-section">
        <h5>Members</h5>
        <label className="tm-filter-row" data-testid="filter-no-members">
          <input type="checkbox" checked={filters.no_members}
            onChange={(e) => set({ no_members: e.target.checked })} /> No members
        </label>
        <label className="tm-filter-row" data-testid="filter-mine-only">
          <input type="checkbox" checked={filters.mine_only}
            onChange={(e) => set({ mine_only: e.target.checked })} /> Cards assigned to me
        </label>
        {members.map((m) => (
          <label key={m.id} className="tm-filter-row" data-testid={`filter-member-${m.id}`}>
            <input type="checkbox" checked={filters.members.includes(m.id)}
              onChange={() => toggleArr('members', m.id)} />
            <div style={{
              width: 22, height: 22, borderRadius: '50%',
              background: m.avatar_url ? `url(${getAvatarUrl(m.avatar_url)}) center/cover` : (m.avatar_color || 'var(--trello-text-muted)'),
              color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, fontWeight: 700,
            }}>{!m.avatar_url && initialsOf(m)}</div>
            <span>{m.full_name}</span>
          </label>
        ))}
      </div>

      <div className="tm-filter-section">
        <h5>Card Status</h5>
        <label className="tm-filter-row" data-testid="filter-status-done">
          <input type="checkbox" checked={filters.status === 'done'}
            onChange={(e) => set({ status: e.target.checked ? 'done' : '' })} /> Marked as complete
        </label>
        <label className="tm-filter-row" data-testid="filter-status-open">
          <input type="checkbox" checked={filters.status === 'not_done'}
            onChange={(e) => set({ status: e.target.checked ? 'not_done' : '' })} /> Not marked as complete
        </label>
      </div>

      <div className="tm-filter-section">
        <h5>Due Date</h5>
        {[
          { k: 'no_date', l: 'No dates' },
          { k: 'overdue', l: 'Overdue' },
          { k: 'today', l: 'Due today' },
          { k: 'week', l: 'Due in next 7 days' },
          { k: 'month', l: 'Due in next 30 days' },
        ].map((opt) => (
          <label key={opt.k} className="tm-filter-row" data-testid={`filter-due-${opt.k}`}>
            <input type="radio" name="due" checked={filters.due === opt.k}
              onChange={() => set({ due: filters.due === opt.k ? '' : opt.k })}
              onClick={() => filters.due === opt.k && set({ due: '' })} />
            {opt.l}
          </label>
        ))}
      </div>

      <div className="tm-filter-section">
        <h5>Priority</h5>
        {Object.entries(PRIORITY_LABEL).map(([k, v]) => (
          <label key={k} className="tm-filter-row" data-testid={`filter-priority-${k}`}>
            <input type="checkbox" checked={filters.priorities.includes(k)}
              onChange={() => toggleArr('priorities', k)} />
            <span className={`tm-pill tm-pill-${k}`}>{v}</span>
          </label>
        ))}
      </div>

      <div className="tm-filter-section">
        <h5>Labels</h5>
        <div className="space-y-1">
          {CARD_LABEL_COLORS.filter((c) => c.value).map((c) => (
            <label key={c.name} className="tm-filter-row" data-testid={`filter-label-${c.name}`}>
              <input type="checkbox" checked={filters.labels.includes(c.value)}
                onChange={() => toggleArr('labels', c.value)} />
              <span className="tm-label-pill" style={{ background: c.value, minWidth: 80 }}>{c.name}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
    </Portal>
  );
}
